import { Link, useParams } from "react-router-dom";

const services = [
  {
    id: "business-establishment",
    program: "Business Logic",
    icon: "fa-building",
    title: "Business Establishment & Formation",
    tagline: "Build Your Foundation Right The First Time",
    desc: "Starting a business is overwhelming. Entity structures, licensing requirements, banking relationships — one wrong move can cost thousands. We eliminate the confusion and guide you through every step.",
    features: ["LLC / Corp / Nonprofit Registration Support", "Multi-State Licensing & Compliance", "Banking & Financial Setup Coordination", "Executive Brand Positioning Advisory", "Non-Profit Assistance & Formation"],
    aside: "The right foundation determines your business's future. We ensure you start strong.",
  },
  {
    id: "business-planning",
    program: "Business Logic",
    icon: "fa-chess",
    title: "Business Planning & Strategy",
    tagline: "Turn Your Vision Into A Winning Strategy",
    desc: "Investors and lenders want to see a solid plan. Without professional documentation, you're leaving money on the table. We create investor-ready business plans and pitch decks.",
    features: ["Corporate Business Plan Development", "Executive / Investor Pitch Deck Creation", "SWOT & Strategic Positioning Analysis", "Comprehensive Market Research Reports"],
    aside: "A great idea without a plan is just a dream. Let's make it a reality.",
  },
  {
    id: "data-analytics",
    program: "Business Logic",
    icon: "fa-chart-bar",
    title: "Data Analytics & Business Intelligence",
    tagline: "Transform Data Into Decisions",
    desc: "You have data but no dashboard. Numbers without insight lead to guesswork, not growth. We build the analytics infrastructure that turns raw data into actionable business intelligence.",
    features: ["KPI Dashboard Design & Implementation", "Sales, Revenue & Customer Analytics", "Predictive Analytics & Forecasting Models", "Market & Competitive Intelligence"],
    aside: "In today's business world, data is your most valuable asset — if you know how to use it.",
  },
  {
    id: "operations",
    program: "Business Logic",
    icon: "fa-cogs",
    title: "Operations & Documentation",
    tagline: "Systematize Your Success",
    desc: "Running everything from your head isn't sustainable. Without documented processes, you can't scale, delegate, or protect yourself. We build the operational backbone your business needs.",
    features: ["Corporate SOP Manual Development", "Employee Handbook Creation", "Pricing Architecture & Internal Matrix", "Process Documentation & Workflow Design"],
    aside: "You can't scale what isn't documented. Let's systematize your success.",
  },
  {
    id: "insurance",
    program: "IAFF",
    icon: "fa-shield-alt",
    title: "Insurance Advisory & Placement",
    tagline: "Protect What You've Built",
    desc: "Most entrepreneurs are dangerously underinsured. One lawsuit, one key employee loss, or one disaster could wipe out everything. We assess your risks and implement comprehensive protection.",
    features: ["Corporate Risk Assessment", "Business Insurance Placement", "Key Person Insurance Strategy", "Group Benefits Strategy Sessions", "Investing: Mutual Funds, Annuities & More", "Portfolio Comparisons & Analysis", "Free Consultations for Investments"],
    aside: "Protection isn't an expense — it's the insurance policy for your ambition.",
  },
  {
    id: "funding",
    program: "IAFF",
    icon: "fa-hand-holding-usd",
    title: "Business Funding & Capital",
    tagline: "Secure The Capital Your Business Needs",
    desc: "Banks say no. Investors don't respond. Grant applications get rejected. Without capital, growth plans remain dreams. We prepare you to successfully secure the funding you need.",
    features: ["Funding Readiness Review & Assessment", "Professional Grant Writing Services", "SBA / Commercial Loan Package Support", "Financial Modeling & Projections", "MBE / WBE / Veteran Certifications"],
    aside: "Capital is the fuel for growth. We help you access it.",
  },
  {
    id: "succession",
    program: "Paradigm Shift Architects",
    icon: "fa-exchange-alt",
    title: "Business Succession Planning",
    tagline: "Plan Your Exit On Your Terms",
    desc: "You've spent years building your business, but without a succession plan, its value could disappear when you step away. We help you maximize value and execute a smooth transition.",
    features: ["Corporate Succession Plan Development", "Business Valuation Coordination", "Transition Training & Knowledge Transfer", "Exit Strategy Planning"],
    aside: "The best time to plan your exit is long before you need one.",
  },
  {
    id: "project-management",
    program: "Paradigm Shift Architects",
    icon: "fa-tasks",
    title: "Project Management",
    tagline: "Execute With Precision",
    desc: "Projects run over budget and behind schedule. Without proper oversight, small issues become expensive problems. We provide professional project management to keep initiatives on track.",
    features: ["Project Plan Development", "Ongoing Oversight & Reporting", "Resource Allocation & Timeline Management", "Risk Mitigation & Contingency Planning"],
    aside: "Every successful outcome starts with disciplined execution.",
  },
  {
    id: "financial-literacy",
    program: "Paradigm Shift Architects",
    icon: "fa-book-open",
    title: "Financial Literacy & Education",
    tagline: "Knowledge Is The Foundation Of Every Successful Business",
    desc: "Too many entrepreneurs make critical financial decisions without understanding the numbers. This leads to cash flow crises and missed opportunities. We empower you with financial knowledge.",
    features: ["Financial Literacy Intro Sessions", "4-Week Corporate Financial Literacy Program", "8-Week Corporate Financial Literacy Cohort", "Enterprise Financial Literacy Workshops"],
    aside: "Financial literacy isn't optional — it's the foundation of business success.",
  },
  {
    id: "notary-services",
    program: "Business Logic",
    icon: "fa-stamp",
    title: "Notary Services",
    tagline: "Licensed & Bonded in the State of Florida",
    desc: "Paradigm Global Business Solutions offers professional notary services to protect your most important documents and transactions. We are licensed and bonded in the State of Florida.",
    features: ["Legacy Planning — Bonded to notarize your will and secure your legacy", "Real Estate Transactions — Notarization of documents related to property purchases", "General Document Notarization — Official notarization for a wide range of legal and business documents"],
    aside: "Protect your most important documents with professional, bonded notary services.",
  },
];

const ServiceDetail = ({ service }) => (
  <main data-testid="service-detail-page">
    <div className="pg-page-hero">
      <div className="pg-container">
        <span className="pg-label">{service.program}</span>
        <h1>{service.title}</h1>
        <p>{service.tagline}</p>
      </div>
    </div>

    <section className="pg-section">
      <div className="pg-container">
        <Link to="/services" style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", color: "var(--magenta)", fontWeight: 600, marginBottom: "2rem", fontSize: ".9rem" }}>
          <i className="fas fa-arrow-left" /> Back to All Services
        </Link>

        <div className="pg-svc-body">
          <div className="pg-svc-text">
            <p style={{ fontSize: "1.05rem", lineHeight: 1.8 }}>{service.desc}</p>
            <h3 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>What's Included</h3>
            <ul>
              {service.features.map((f, i) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/consultation" className="pg-btn pg-btn-primary" style={{ marginTop: "1.5rem" }}>
              <i className="fas fa-calendar-check" /> Book a Call
            </Link>
          </div>
          <div className="pg-svc-aside">
            <h4 style={{ marginBottom: ".6rem", color: "var(--black)" }}>Why This Matters</h4>
            <div className="pg-svc-quote">{service.aside}</div>
            <div style={{ marginTop: "1.2rem" }}>
              <p style={{ fontSize: ".85rem", color: "var(--muted)" }}>
                <strong style={{ color: "var(--magenta)" }}>Program:</strong> {service.program}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="pg-cta-banner">
      <div className="pg-container">
        <h2>Ready to Get Started?</h2>
        <p>Book a complimentary consultation to discuss your needs and receive a customized proposal.</p>
        <div className="pg-cta-btns">
          <Link to="/book" className="pg-btn pg-btn-white">Book Your Free Call</Link>
        </div>
      </div>
    </section>
  </main>
);

export const ServicesPage = () => {
  const { serviceId } = useParams();

  if (serviceId) {
    const svc = services.find(s => s.id === serviceId);
    if (!svc) {
      return (
        <main style={{ paddingTop: "var(--nav-h)", minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="pg-text-center">
            <h1>Service Not Found</h1>
            <Link to="/services" className="pg-btn pg-btn-primary" style={{ marginTop: "1rem" }}>View All Services</Link>
          </div>
        </main>
      );
    }
    return <ServiceDetail service={svc} />;
  }

  const programGroups = [
    { id: "business-logic", label: "Program One", name: "Business Logic", desc: "Strategic foundation, planning, operations, analytics, and notary services for your business.", ids: ["business-establishment", "business-planning", "data-analytics", "operations", "notary-services"] },
    { id: "iaff", label: "Program Two", name: "Insurance + Assurance = Financial Freedom", desc: "Risk assessment, insurance appointment & placement, business funding.", ids: ["insurance", "funding"] },
    { id: "paradigm-shift", label: "Program Three", name: "Paradigm Shift Architects", desc: "Transformation, coaching, wellness, succession, and growth advisory.", ids: ["succession", "project-management", "financial-literacy"] },
  ];

  return (
    <main data-testid="services-page">
      <div className="pg-page-hero">
        <div className="pg-container">
          <span className="pg-label">Our Services</span>
          <h1>Driving Your Business Forward</h1>
          <p>10 detailed service areas across 3 programs</p>
        </div>
      </div>

      {programGroups.map((group) => (
        <section key={group.id} className="pg-section" id={group.id} style={{ background: group.id === "iaff" ? "var(--bg-light)" : undefined }}>
          <div className="pg-container">
            <div className="pg-text-center">
              <span className="pg-label">{group.label}</span>
              <h2>{group.name}</h2>
              <div className="pg-divider" />
              <p className="pg-intro">{group.desc}</p>
            </div>

            {group.ids.map((svcId) => {
              const svc = services.find(s => s.id === svcId);
              if (!svc) return null;
              return (
                <div className="pg-svc-block" key={svc.id} style={{ borderBottom: "1px solid var(--border)", paddingBottom: "2.5rem", marginBottom: "2.5rem" }}>
                  <div className="pg-svc-header">
                    <div className="pg-svc-ico"><i className={`fas ${svc.icon}`} /></div>
                    <div>
                      <h3 style={{ margin: 0 }}>{svc.title}</h3>
                      <p style={{ margin: 0, fontSize: ".88rem", color: "var(--magenta)", fontWeight: 600 }}>{svc.tagline}</p>
                    </div>
                  </div>
                  <p style={{ color: "var(--muted)", maxWidth: 700 }}>{svc.desc}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginTop: "1rem" }}>
                    {svc.features.map((f, j) => (
                      <span key={f} style={{
                        background: "var(--bg-pink)", padding: ".35rem .8rem", borderRadius: 50,
                        fontSize: ".82rem", color: "var(--magenta)", fontWeight: 500
                      }}>
                        {f}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: "1rem" }}>
                    <Link to={`/services/${svc.id}`} style={{ color: "var(--magenta)", fontWeight: 600, fontSize: ".9rem" }}>
                      Learn More <i className="fas fa-arrow-right" style={{ fontSize: ".75rem" }} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <section className="pg-cta-banner">
        <div className="pg-container">
          <h2>Not Sure Where to Start?</h2>
          <p>Book a free strategy call and we'll recommend the services that will have the biggest impact.</p>
          <div className="pg-cta-btns">
            <Link to="/consultation" className="pg-btn pg-btn-white">
              <i className="fas fa-calendar-check" /> Schedule Strategy Session
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
